
import React, { useState, useMemo } from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import { cn } from '@/lib/utils';
import { RouteConfig } from './RouteConfig';
import { ChevronDown, ChevronRight } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { useAuth } from '@/contexts/AuthContext';

interface SidebarNavItemProps {
  route: RouteConfig;
  isOpen: boolean;
}

// Helper function to check if any child (or nested child) is active
const hasActiveDescendant = (children: RouteConfig[] | undefined, pathname: string): boolean => {
  if (!children) return false;
  return children.some(child =>
    child.path === pathname || hasActiveDescendant(child.children, pathname)
  );
};

const filterChildrenByRole = (children: RouteConfig[] | undefined, role: string | undefined | null): RouteConfig[] => {
  if (!children || !role) return children || [];
  return children
    .filter(child => !child.roles || child.roles.includes(role))
    .map(child => ({
      ...child,
      children: child.children ? filterChildrenByRole(child.children, role) : undefined,
    }));
};

const SidebarNavItem: React.FC<SidebarNavItemProps> = ({ route, isOpen }) => {
  const location = useLocation();
  const { userRole } = useAuth();
  const [isExpanded, setIsExpanded] = useState(false);

  const filteredChildren = useMemo(() =>
    filterChildrenByRole(route.children, userRole),
    [route.children, userRole]
  );

  const isActive = location.pathname === route.path;
  const hasActiveChild = hasActiveDescendant(filteredChildren, location.pathname);
  const shouldExpand = hasActiveChild || isExpanded;

  const handleToggle = () => {
    if (filteredChildren.length > 0 && isOpen) {
      setIsExpanded(!isExpanded);
    }
  };

  const IconComponent = route.icon;

  if (filteredChildren.length > 0) {
    return (
      <div className="space-y-1">
        <button style={{ position: 'relative' }}
          onClick={handleToggle}
          className={cn(
            "group flex w-full items-center gap-3 rounded-xl px-3 py-2 text-left text-sm transition-all duration-300",
            "hover:bg-harvest-primary/10 hover:text-harvest-primary shadow-sm hover:shadow-md",
            (isActive || hasActiveChild) && "bg-harvest-primary text-white font-medium shadow-harvest-primary/20 shadow-lg",
            !isOpen && "justify-center px-2"
          )}
          title={!isOpen ? route.name : undefined}
        >
          <IconComponent className={cn(
            "h-4 w-4 flex-shrink-0 transition-transform duration-300 group-hover:scale-110",
            (isActive || hasActiveChild) && "scale-110"
          )} />
          {isOpen && (
            <>
              <span className="flex-1 truncate">{route.name}</span>
              {route.badgeCount !== undefined && route.badgeCount > 0 && (
                <Badge
                  className={cn(
                    "h-5 min-w-5 px-1.5 text-[10px] border-none",
                    (isActive || hasActiveChild) ? "bg-white text-harvest-primary" : "bg-harvest-error text-white"
                  )}
                >
                  {route.badgeCount}
                </Badge>
              )}
              <div className={cn(
                "transition-transform duration-300",
                shouldExpand && "rotate-180"
              )}>
                <ChevronDown className="h-3.5 w-3.5 opacity-50" />
              </div>
            </>
          )}
          {!isOpen && route.badgeCount !== undefined && route.badgeCount > 0 && (
            <span className="absolute -top-1 -right-1 h-4 min-w-4 rounded-full bg-harvest-error text-white text-[9px] flex items-center justify-center border-2 border-sidebar-background shadow-sm">
              {route.badgeCount > 9 ? '9+' : route.badgeCount}
            </span>
          )}
        </button>

        {shouldExpand && isOpen && (
          <div className="ml-4 pl-4 border-l border-sidebar-border/30 space-y-1 mt-1">
            {filteredChildren.map((child) => {
              if (child.children) {
                return (
                  <SidebarNavItem
                    key={child.name}
                    route={child}
                    isOpen={isOpen}
                  />
                );
              }

              const ChildIcon = child.icon;

              return (
                <NavLink
                  key={child.path}
                  to={child.path || '#'}
                  className={({ isActive }) =>
                    cn(
                      "group flex items-center gap-3 rounded-lg px-3 py-2 text-sm transition-all duration-300",
                      "hover:bg-harvest-secondary/10 hover:text-harvest-secondary",
                      isActive && "bg-harvest-secondary/10 text-harvest-secondary font-medium"
                    )
                  }
                >
                  {ChildIcon && <ChildIcon className="h-3.5 w-3.5 flex-shrink-0 transition-transform group-hover:rotate-12" />}
                  <span className="truncate">{child.name}</span>
                </NavLink>
              );
            })}
          </div>
        )}
      </div>
    );
  }

  return (
    <NavLink
      to={route.path || '#'}
      className={({ isActive }) =>
        cn(
          "group flex items-center gap-3 rounded-xl px-3 py-2 text-sm transition-all duration-300 relative",
          "hover:bg-harvest-primary/10 hover:text-harvest-primary shadow-sm hover:shadow-md",
          isActive && "bg-harvest-primary text-white font-medium shadow-harvest-primary/20 shadow-lg",
          !isOpen && "justify-center px-2"
        )
      }
      title={!isOpen ? route.name : undefined}
    >
      <IconComponent className={cn(
        "h-4 w-4 flex-shrink-0 transition-transform duration-300 group-hover:scale-110",
        isActive && "scale-110"
      )} />
      {isOpen && <span className="truncate">{route.name}</span>}
      {isActive && !isOpen && (
        <div className="absolute right-0 top-1/2 -translate-y-1/2 w-1 h-6 bg-harvest-primary rounded-l-full" />
      )}
    </NavLink>
  );
};

export default SidebarNavItem;
